"use client";

// Pages/PaymentReturnTakbull/PaymentReturnTakbull.jsx
// Роут:
// <Route
//     path="/:lang/payment-return-takbull"
//     element={<PaymentReturnTakbull />}
// />

import { useEffect, useRef, useState } from "react";
import {
    useNavigate,
    useParams,
    useSearchParams,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import {
    CheckCircle2,
    CircleAlert,
    LoaderCircle,
} from "lucide-react";

import { getServerUrl } from "../../Utils/serverSelector";
import { getJwtToken } from "../../Utils/authStorage";

import "./payment-return-takbull.css";

const getErrorMessage = (error, fallback) =>
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.message ||
    String(error || fallback);

export default function PaymentReturnTakbull() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { lang = "ru" } = useParams();
    const [params] = useSearchParams();

    const ranRef = useRef(false);

    const [state, setState] = useState("processing");
    // processing | ok | fail

    const [errorMessage, setErrorMessage] = useState("");

    const status = params.get("status");
    const orderId = params.get("order") || "";
    const amountFromUrl = params.get("amount") || "";
    const backPathFromUrl = params.get("return") || "";
    const masterLinkFromUrl = params.get("master") || "";

    const [resolvedMasterLink, setResolvedMasterLink] = useState(masterLinkFromUrl);

    useEffect(() => {
        if (ranRef.current) return;
        ranRef.current = true;

        const run = async () => {
            if (status !== "ok") {
                setState("fail");
                setErrorMessage(
                    t("payment_failed_msg") ||
                    "Оплата не была завершена"
                );
                return;
            }

            if (!orderId) {
                setState("fail");
                setErrorMessage(
                    t("payment_order_not_found") ||
                    "Не найден номер платежа"
                );
                return;
            }

            const storageKey =
                `takbull_pending_signup_v2_${orderId}`;

            let pending = null;

            try {
                const raw = sessionStorage.getItem(storageKey);

                console.log("[TAKBULL RETURN]", {
                    orderId,
                    storageKey,
                    found: Boolean(raw),
                    keys: Object.keys(sessionStorage),
                });

                if (raw) {
                    pending = JSON.parse(raw);

                    const savedMasterLink =
                        pending?.masterLink ||
                        masterLinkFromUrl;

                    if (savedMasterLink) {
                        setResolvedMasterLink(
                            savedMasterLink
                        );
                    }
                }
            } catch (error) {
                console.error(
                    "[TAKBULL RETURN] sessionStorage read failed",
                    error
                );
            }

            const signupBody = pending?.signupBody;

            if (!signupBody) {
                setState("fail");
                setErrorMessage(
                    t("payment_data_not_found") ||
                    "Оплата прошла, но данные заявки не найдены"
                );
                return;
            }

            const passwordLength = String(
                signupBody.password || ""
            ).trim().length;

            if (passwordLength < 8) {
                setState("fail");
                setErrorMessage(
                    t("password_min") ||
                    "Password must contain at least 8 characters"
                );
                return;
            }

            try {
                const jwt = await getJwtToken();

                const headers = {
                    "Content-Type": "application/json",
                    ...(jwt
                        ? {
                            Authorization: `Bearer ${jwt}`,
                        }
                        : {}),
                };

                console.log(
                    "[TAKBULL RETURN] creating signup after payment",
                    {
                        orderId,
                        uid: signupBody.uid,
                        email: signupBody.email,
                        passwordLength,
                        hasMerchantDetails: Boolean(
                            signupBody.merchantDetails
                        ),
                        hasConnect: Boolean(
                            signupBody.connect
                        ),
                    }
                );

                /*
                 * 1. Только сейчас, после status=ok,
                 *    создаём заявку Takbull.
                 */
                const signupResponse = await axios.post(
                    `${getServerUrl()}/payments/takbull/signup`,
                    signupBody,
                    {
                        headers,
                        withCredentials: false,
                        timeout: 30000,
                    }
                );

                const signupData = signupResponse?.data || {};

                const createdLeadId =
                    signupData?.leadId ??
                    signupData?.id ??
                    signupData?.lead?.id ??
                    null;

                /*
                 * 2. После успешного создания заявки отмечаем,
                 *    что установочный платёж выполнен.
                 *
                 * Если endpoint /signup уже сам сохраняет оплату,
                 * этот запрос можно удалить.
                 */
                try {
                    await axios.post(
                        `${getServerUrl()}/payments/takbull/signup/paid`,
                        {
                            leadId:
                                Number(createdLeadId) || null,
                            orderId:
                                orderId ||
                                pending?.orderId ||
                                null,
                            amount:
                                Number(
                                    amountFromUrl ||
                                    pending?.amount
                                ) || null,
                        },
                        {
                            headers,
                            withCredentials: false,
                            timeout: 20000,
                        }
                    );
                } catch (paidError) {
                    /*
                     * Заявка уже создана.
                     * Ошибка отметки paid не должна создавать
                     * заявку повторно при обновлении страницы.
                     */
                    console.error(
                        "[TAKBULL RETURN] mark paid failed",
                        paidError
                    );
                }

                try {
                    sessionStorage.removeItem(storageKey);
                } catch {
                    // Ничего страшного.
                }

                /*
                 * Убираем query-параметры оплаты.
                 * После F5 заявка не отправится повторно.
                 */
                window.history.replaceState(
                    {},
                    "",
                    window.location.pathname
                );

                setState("ok");
            } catch (error) {
                console.error(
                    "[TAKBULL RETURN] signup failed",
                    error
                );

                setState("fail");
                setErrorMessage(
                    getErrorMessage(
                        error,
                        t("something_went_wrong") ||
                        "Не удалось сохранить данные подключения"
                    )
                );
            }
        };

        run();
    }, [
        status,
        orderId,
        amountFromUrl,
        t,
    ]);

    const goHome = () => {
        const safeMasterLink =
            resolvedMasterLink ||
            masterLinkFromUrl;

        const targetPath = safeMasterLink
            ? `/${lang}/master/${encodeURIComponent(
                safeMasterLink
            )}/master-dashboard`
            : `/${lang}/master-dashboard`;

        navigate(targetPath, {
            replace: true,
            state: {
                activeTab: "home",
                takbullSignupCompleted:
                    state === "ok",
            },
        });
    };

    const goBack = () => {
        if (backPathFromUrl) {
            navigate(backPathFromUrl, {
                replace: true,
            });
            return;
        }

        goHome();
    };

    return (
        <main className="tpr-root">
            <section className="tpr-card">
                {state === "processing" && (
                    <>
                        <LoaderCircle
                            className="tpr-icon tpr-spinner"
                            size={60}
                            strokeWidth={1.8}
                        />

                        <h1 className="tpr-title">
                            {t("processing_payment") ||
                                "Завершаем подключение"}
                        </h1>

                        <p className="tpr-text">
                            {t(
                                "please_do_not_close_page"
                            ) ||
                                "Сохраняем данные подключения. Не закрывайте эту страницу."}
                        </p>
                    </>
                )}

                {state === "ok" && (
                    <>
                        <CheckCircle2
                            className="tpr-icon tpr-success"
                            size={68}
                            strokeWidth={1.7}
                        />

                        <h1 className="tpr-title">
                            {t("takbull_fee_paid_ok") ||
                                "Всё готово"}
                        </h1>

                        <p className="tpr-text">
                            {t("we_will_contact_you") ||
                                "Оплата прошла успешно, данные сохранены. Мы свяжемся с вами для завершения подключения."}
                        </p>

                        <button
                            type="button"
                            className="tpr-primary"
                            onClick={goHome}
                        >
                            {t("great") || "Отлично"}
                        </button>
                    </>
                )}

                {state === "fail" && (
                    <>
                        <CircleAlert
                            className="tpr-icon tpr-error"
                            size={68}
                            strokeWidth={1.7}
                        />

                        <h1 className="tpr-title">
                            {t(
                                "connection_not_completed"
                            ) ||
                                "Не удалось завершить подключение"}
                        </h1>

                        <p className="tpr-text tpr-error-text">
                            {errorMessage}
                        </p>

                        <button
                            type="button"
                            className="tpr-primary"
                            onClick={goBack}
                        >
                            {t("back") || "Назад"}
                        </button>
                    </>
                )}
            </section>
        </main>
    );
}
